import React, { useState, useId } from 'react';
import { X, Send, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { apiService } from '../services/api';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipient: {
    display_name: string | null;
    email_address: string | null;
    user_account_id?: string;
  };
}

const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose, recipient }) => {
  const uid = useId();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Escape closes it, as it does for a side panel (not while an email is going out).
  const sendingRef = React.useRef(sending);
  sendingRef.current = sending;
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !sendingRef.current) closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim()) return;
    if (!recipient.email_address) {
      setStatus({ type: 'error', text: 'This user has no email address on record.' });
      return;
    }

    setSending(true);
    setStatus(null);
    try {
      const resp = await apiService.sendNotificationEmail({
        toEmail: recipient.email_address,
        toName: recipient.display_name || recipient.email_address,
        subject: subject.trim() || 'Notification from Jarvis',
        message: message.trim(),
      });

      if (resp.data?.success) {
        setStatus({ type: 'success', text: `Email sent to ${recipient.email_address}` });
        setMessage('');
        setSubject('');
        // Auto-close after 2 s on success
        setTimeout(onClose, 2000);
      } else {
        setStatus({ type: 'error', text: resp.data?.error || 'Failed to send email.' });
      }
    } catch (err: any) {
      const detail = err.response?.data?.error || err.response?.data?.details || err.message;
      setStatus({ type: 'error', text: detail || 'Failed to send email.' });
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    setSubject('');
    setMessage('');
    setStatus(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div role="presentation" className="absolute inset-0 bg-black bg-opacity-40" onClick={handleClose} />

      {/* Modal */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-title`}
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-md z-10 overflow-hidden focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-white" aria-hidden />
            <h2 id={`${uid}-title`} className="text-sm font-semibold text-white">Send notification</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={sending}
            aria-label="Close"
            className="p-1 rounded hover:bg-blue-400 transition-colors"
          >
            <X className="h-4 w-4 text-white" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Recipient */}
          <div className="flex items-center gap-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-200 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold uppercase">
              {(recipient.display_name || recipient.email_address || '?')[0]}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-900 truncate">
                {recipient.display_name || 'Unknown User'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {recipient.email_address || <span className="text-red-700">No email on record</span>}
              </p>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor={`${uid}-1`} className="block text-xs font-medium text-gray-700 mb-1">
              Subject <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              id={`${uid}-1`}
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Notification from Jarvis"
              disabled={sending}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
            />
          </div>

          {/* Message */}
          <div>
            <label htmlFor={`${uid}-2`} className="block text-xs font-medium text-gray-700 mb-1">
              Message <span className="text-red-700">*</span>
            </label>
            <textarea
              id={`${uid}-2`}
              data-autofocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={5}
              disabled={sending}
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-50"
            />
            <p className="text-xs text-gray-500 mt-1 text-right">{message.length} chars</p>
          </div>

          {/* Status */}
          {status && (
            <div role={status.type === 'error' ? 'alert' : 'status'} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
              status.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {status.type === 'success'
                ? <CheckCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                : <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              }
              <span>{status.text}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={sending}
            className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !message.trim() || !recipient.email_address}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Sending…
              </>
            ) : (
              <>
                <Send className="h-3.5 w-3.5" />
                Send email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
